/* eslint-disable no-underscore-dangle, no-nested-ternary */
const { internalLogger } = require('internal-logger');
const fs = require('fs');
const objectPath = require('object-path');
const http = require('lc-http');
const { join } = require('path');
const Reader = require('properties-reader');
const MODULE_NAME = require('./module-name');
const config = require('./default-config');

const lcMergedPropertiesPath = join(
  process.cwd(),
  './config/lc-merged.properties'
);
const hasPropertiesFile = fs.existsSync(lcMergedPropertiesPath);
const DEFAULT_PROFILE = undefined;
const DEFAULT_SUB_PROFILE = 'default';
const DEFAULT_APP_ID = 'default';

let appEtcdKeys;
let etcd = hasPropertiesFile ? Reader(lcMergedPropertiesPath)._properties : {};

// setup a decrypted properties cache
etcd.__decrypted__ = {};

/**
 * Client for polling the ETCD service endpoint.
 * @module
 * @param {Object} context - The context of the application
 * @param {String} context.environment - Node environment of the application.
 * @param {String} context.subEnvironment - Sub environment of the application.
 * @param {String} context.appId - Name of the application.
 * @returns {Object} - Returns two publicly accessible methods
 */
function ETCD(context = {}) {
  const { LC_PROFILE, LC_SUB_PROFILE, LC_APP_ID } = process.env;
  const env =
    LC_PROFILE || context.environment || etcd.LC_PROFILE || DEFAULT_PROFILE;
  const subEnv =
    LC_SUB_PROFILE ||
    context.subEnvironment ||
    etcd.LC_SUB_PROFILE ||
    DEFAULT_SUB_PROFILE;
  const appId = LC_APP_ID || context.appId || etcd.LC_APP_ID || DEFAULT_APP_ID;

  const url = `${config.etcdClient.url}/${env}/${subEnv}/${appId}`;

  /**
   * Normalize any error that may come from the client
   * @private
   */
  function captureError(err, res, body) {
    const statusCode =
      objectPath.get(res, 'statusCode') ||
      objectPath.get(err, 'response.status', 500);
    if (statusCode === 304) return { statusCode };
    const errorsInBody =
      objectPath.get(body, 'errors') || objectPath.get(body, 'error');
    if (statusCode === 200 && !errorsInBody && !err) return undefined;
    const errorResponse = {
      status: statusCode,
      message:
        'Something unknown happened while trying to retrieve ETCD config.',
      errors: errorsInBody || err,
    };

    internalLogger.warn(
      {
        url,
        err: errorResponse,
        context,
      },
      `${MODULE_NAME}: Could not get ETCD config.`
    );

    return errorResponse;
  }

  /**
   * Get the latest ECTD dynamic configurations.
   * @function refresh
   * @param {Function} cb - optional callback upon response
   */
  async function refresh(cb) {
    internalLogger.trace(`${MODULE_NAME}: loading etcd`);
    const headers =
      etcd && etcd.clientRawDataSha1
        ? { clientRawDataSha1: etcd.clientRawDataSha1 }
        : {};
    const options = {
      headers,
      timeout: config.etcdClient.timeout,
      json: true,
    };

    const promise = new Promise((resolve, reject) => {
      // eslint-disable-next-line no-promise-executor-return
      return http
        .get(url, options)
        .then((response) => {
          if (response.statusCode === 304) {
            return cb ? cb(null, undefined) : resolve(undefined);
          }

          // clear the __decrypted__ cache upon refresh
          etcd.__decrypted__ = {};

          // then merge new and existing properties
          etcd = {
            ...etcd,
            ...response.data,
          };

          // if we've already refreshed once, prune old keys from incoming new keys
          if (appEtcdKeys) {
            appEtcdKeys.forEach((key) => {
              if (!Object.prototype.hasOwnProperty.call(response.data, key)) {
                delete etcd[key];
              }
            });
          }

          appEtcdKeys = Object.keys(response.data || {});

          const msg = `${MODULE_NAME}: Successfully retrieved ETCD config.`;
          internalLogger.info(msg);

          return cb ? cb(null, etcd) : resolve(etcd);
        })
        .catch((error) => {
          const resolvedError = captureError(error, null, null);

          if (resolvedError && resolvedError.statusCode !== 304) {
            // Targets error other than 304 to let the consumer act on it
            return cb ? cb(resolvedError) : reject(resolvedError);
          }
          const msg = `${MODULE_NAME}: No changes to ETCD config, using same values.`;
          if (resolvedError && resolvedError.statusCode === 304) {
            internalLogger.debug(msg);
          }
          return cb ? cb(resolvedError) : resolve(undefined);
        });
    });

    if (global._etcdClient) {
      global._etcdClient.promise = promise;
    }
    return promise;
  }

  /**
   * Get the value of an object based on the provided key.
   * @function get
   *
   * @param {String} key Any valid path key lookup for an object. i.e.
   * `someKey`, `some.key`, `some-key`, etc...
   * @return {String|Number|Boolean} All ETCD values are strings but we check
   * for possible coercion cases – booleans and numbers – where possible.
   */
  function get(key) {
    if (
      typeof key !== 'string' ||
      !etcd ||
      !Object.prototype.hasOwnProperty.call(etcd, key)
    ) {
      return undefined;
    }

    const value = etcd[key];
    internalLogger.trace(
      {
        key,
        value,
      },
      `${MODULE_NAME}: Getting raw ETCD value.`
    );

    // NOTE: All ETCD values are strings (no other primitives are supported).
    // However, if that ever changes, we want to be ready for it and just
    // return the value given if it's not of type `string`.
    if (typeof value !== 'string') {
      return value;
    }

    // Running through a variety of checks to see if the value can be converted
    // to either a boolean or number; otherwise we just return it as a string.
    if (value === 'true') return true;
    if (value === 'false') return false;

    // Value can be coerced to a number
    let coercedValue;
    // eslint-disable-next-line no-restricted-globals
    if (!isNaN(value)) {
      coercedValue = parseFloat(value);
    }

    // Support falsy 0 value
    if (coercedValue === 0) return coercedValue;

    // coercedValue might be NaN so in that case, return original value
    return coercedValue || value;
  }

  /**
   * Checks if the value (based on the provided key) exists on ETCD.
   * @function get
   *
   * @param {String} key Any valid path key lookup for an object. i.e.
   * `someKey`, `some.key`, `some-key`, etc...
   * @return {Boolean} When the value exists returns true, false otherwise.
   */
  function has(key) {
    return typeof key !== 'string' ||
      !etcd ||
      !Object.prototype.hasOwnProperty.call(etcd, key)
      ? false
      : etcd[key] !== undefined;
  }

  /**
   * Get the value of an encrypted ETCD property based on the provided key and then decrypt it with PEG.
   * @async
   * @function getEncrypted
   *
   * @param {String} key Any valid path key lookup for an ETCD property
   * @return {Promise<String|Number|Boolean>} All ETCD values are strings but we check
   * for possible coercion cases – booleans and numbers – where possible.
   */
  async function getEncrypted(key) {
    if (has(key)) {
      const cachedDecryptedValue = etcd.__decrypted__[key];

      if (!cachedDecryptedValue) {
        const encryptedValue = get(key);
        const hostname = config.etcdClient.pegUrl;
        const endpoint = '/api/encryption-gateway/decryptProperties';
        const props = {};

        props[key] = encryptedValue;

        internalLogger.trace(
          {
            key,
            value: encryptedValue,
          },
          `${MODULE_NAME}: Getting decrypted PEG value.`
        );

        const { decryptedProperties } = (
          await http
            .post(`https://${hostname}${endpoint}`, {
              body: JSON.stringify(props),
            })
            .catch((error) => {
              internalLogger.warn(
                {
                  url: `https://${hostname}${endpoint}`,
                  error,
                },
                `${MODULE_NAME}: Could not decrypt ETCD property from PEG.`
              );
            })
        ).data;

        etcd.__decrypted__[key] = decryptedProperties[key].decrypted;

        return etcd.__decrypted__[key];
      }

      return cachedDecryptedValue;
    }

    return Promise.resolve(undefined);
  }

  return {
    refresh,
    get,
    getEncrypted,
    has,
  };
}

module.exports = ETCD;

